import { useEffect, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { getPositionGroup } from '../../data/football'
import type { PitchObject, PitchObjectType, Player, TacticFrame } from '../../types/domain'
import { uid } from '../../utils/id'

export type BoardTool = 'select' | PitchObjectType | 'delete'
type Point = { x: number; y: number }
type DrawingState = Point & { currentX: number; currentY: number }

const clamp = (value: number, min = 1, max = 99) => Math.max(min, Math.min(max, value))
const lerp = (from: number, to: number, progress: number) => from + (to - from) * progress
const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3)

function interpolateObject(source: PitchObject | undefined, target: PitchObject, progress: number): PitchObject {
  if (!source || source.type !== target.type) return target
  return {
    ...target,
    x: lerp(source.x, target.x, progress),
    y: lerp(source.y, target.y, progress),
    endX: target.endX === undefined ? undefined : lerp(source.endX ?? target.endX, target.endX, progress),
    endY: target.endY === undefined ? undefined : lerp(source.endY ?? target.endY, target.endY, progress),
    width: target.width === undefined ? undefined : lerp(source.width ?? target.width, target.width, progress),
    height: target.height === undefined ? undefined : lerp(source.height ?? target.height, target.height, progress),
  }
}

function interpolateFrame(source: TacticFrame, target: TacticFrame, progress: number): TacticFrame {
  return {
    ...target,
    placements: target.placements.map(placement => {
      const previous = source.placements.find(item => item.playerId === placement.playerId)
      return previous ? { ...placement, x:lerp(previous.x, placement.x, progress), y:lerp(previous.y, placement.y, progress) } : placement
    }),
    objects: target.objects.map(object => interpolateObject(source.objects.find(item => item.id === object.id), object, progress)),
  }
}
function pointFromEvent(event: ReactPointerEvent, element: HTMLElement): Point {
  const rect = element.getBoundingClientRect()
  return { x: clamp(((event.clientX - rect.left) / rect.width) * 100), y: clamp(((event.clientY - rect.top) / rect.height) * 100) }
}

function translateObject(object: PitchObject, dx: number, dy: number): PitchObject {
  const xs = [object.x, object.endX ?? object.x, object.x + (object.width ?? 0)]
  const ys = [object.y, object.endY ?? object.y, object.y + (object.height ?? 0)]
  const safeDx = Math.max(1 - Math.min(...xs), Math.min(99 - Math.max(...xs), dx))
  const safeDy = Math.max(1 - Math.min(...ys), Math.min(99 - Math.max(...ys), dy))
  return {
    ...object,
    x: object.x + safeDx,
    y: object.y + safeDy,
    endX: object.endX === undefined ? undefined : object.endX + safeDx,
    endY: object.endY === undefined ? undefined : object.endY + safeDy,
  }
}

export function Pitch({ frame, players, tool, selectedPlayerId, selectedObjectId, playerViewId, animateTransitions = false, onChange, onBeginInteraction, onSelectPlayer, onSelectObject }: {
  frame: TacticFrame
  players: Player[]
  tool: BoardTool
  selectedPlayerId?: string
  selectedObjectId?: string
  playerViewId?: string
  animateTransitions?: boolean
  onChange: (frame: TacticFrame, options?: { skipHistory?: boolean }) => void
  onBeginInteraction?: () => void
  onSelectPlayer: (id?: string) => void
  onSelectObject: (id?: string) => void
}) {
  const pitchRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef(frame)
  const displayFrameRef = useRef(frame)
  const previousTargetId = useRef(frame.id)
  const animationFrame = useRef<number>()
  const objectDrag = useRef<{ id: string; last: Point; historyStarted: boolean }>()
  const playerInteractionStarted = useRef(false)
  const [drawing, setDrawing] = useState<DrawingState>()
  const [draggingPlayer, setDraggingPlayer] = useState<string>()
  const [displayFrame, setDisplayFrame] = useState(frame)

  useEffect(() => { frameRef.current = frame }, [frame])
  useEffect(() => {
    if (animationFrame.current !== undefined) cancelAnimationFrame(animationFrame.current)
    const changedPhase = previousTargetId.current !== frame.id
    previousTargetId.current = frame.id
    if (!animateTransitions || !changedPhase) {
      displayFrameRef.current = frame
      setDisplayFrame(frame)
      return
    }

    const source = displayFrameRef.current
    const startedAt = performance.now()
    const duration = 950
    const tick = (timestamp: number) => {
      const progress = Math.min(1, (timestamp - startedAt) / duration)
      const next = interpolateFrame(source, frame, easeOutCubic(progress))
      displayFrameRef.current = next
      setDisplayFrame(next)
      if (progress < 1) animationFrame.current = requestAnimationFrame(tick)
      else {
        animationFrame.current = undefined
        displayFrameRef.current = frame
        setDisplayFrame(frame)
      }
    }
    animationFrame.current = requestAnimationFrame(tick)
    return () => {
      if (animationFrame.current !== undefined) cancelAnimationFrame(animationFrame.current)
    }
  }, [animateTransitions, frame])
  const emitChange = (next: TacticFrame, skipHistory = false) => { frameRef.current = next; onChange(next, { skipHistory }) }
  const playerById = (id: string) => players.find(player => player.id === id)
  const updatePlacement = (playerId: string, x: number, y: number) => emitChange({ ...frameRef.current, placements: frameRef.current.placements.map(placement => placement.playerId === playerId ? { ...placement, x, y } : placement) }, true)

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedObjectId && !['INPUT','TEXTAREA'].includes((event.target as HTMLElement).tagName)) {
        emitChange({ ...frameRef.current, objects: frameRef.current.objects.filter(object => object.id !== selectedObjectId) })
      }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [selectedObjectId])

  const removeObject = (id: string) => {
    emitChange({ ...frameRef.current, objects: frameRef.current.objects.filter(object => object.id !== id) })
    onSelectObject(undefined)
  }
  const startObjectDrag = (event: ReactPointerEvent<SVGElement | HTMLButtonElement>, id: string) => {
    event.stopPropagation()
    if (tool === 'delete') { removeObject(id); return }
    if (!pitchRef.current) return
    onSelectPlayer(undefined)
    onSelectObject(id)
    objectDrag.current = { id, last: pointFromEvent(event, pitchRef.current), historyStarted:false }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget && (event.target as HTMLElement).closest('[data-interactive]')) return
    onSelectPlayer(undefined)
    onSelectObject(undefined)
    if (!pitchRef.current || ['select','delete'].includes(tool)) return
    const point = pointFromEvent(event, pitchRef.current)
    setDrawing({ ...point, currentX:point.x, currentY:point.y })
  }
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!pitchRef.current) return
    const point = pointFromEvent(event, pitchRef.current)
    if (objectDrag.current) {
      const { id, last, historyStarted } = objectDrag.current
      const object = frameRef.current.objects.find(item => item.id === id)
      if (object) {
        if (!historyStarted) onBeginInteraction?.()
        const moved = translateObject(object, point.x - last.x, point.y - last.y)
        emitChange({ ...frameRef.current, objects: frameRef.current.objects.map(item => item.id === id ? moved : item) }, true)
        objectDrag.current = { id, last:point, historyStarted:true }
      }
      return
    }
    if (drawing) setDrawing(current => current ? { ...current, currentX:point.x, currentY:point.y } : current)
  }
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (objectDrag.current) { objectDrag.current = undefined; return }
    if (!drawing || !pitchRef.current) return
    const end = pointFromEvent(event, pitchRef.current)
    const object: PitchObject = { id:uid(), type:tool as PitchObjectType, x:drawing.x, y:drawing.y }
    if (['arrow','dashed-arrow','line'].includes(tool)) Object.assign(object, { endX:end.x, endY:end.y })
    if (tool === 'zone') Object.assign(object, { x:Math.min(drawing.x,end.x), y:Math.min(drawing.y,end.y), width:Math.max(2,Math.abs(end.x-drawing.x)), height:Math.max(2,Math.abs(end.y-drawing.y)) })
    if (tool === 'ball') Object.assign(object, { x:end.x, y:end.y })
    if (tool === 'text') Object.assign(object, { x:end.x, y:end.y, text:'ТАКТИЧЕСКАЯ ЗОНА' })
    emitChange({ ...frameRef.current, objects:[...frameRef.current.objects, object] })
    onSelectObject(object.id)
    setDrawing(undefined)
  }
  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    const id = event.dataTransfer.getData('playerId')
    if (!id || !pitchRef.current || frameRef.current.placements.some(item => item.playerId === id)) return
    const rect = pitchRef.current.getBoundingClientRect()
    emitChange({ ...frameRef.current, placements:[...frameRef.current.placements,{ playerId:id, x:clamp(((event.clientX-rect.left)/rect.width)*100), y:clamp(((event.clientY-rect.top)/rect.height)*100) }] })
  }

  const previewLine = drawing && ['arrow','dashed-arrow','line'].includes(tool)
  const previewZone = drawing && tool === 'zone'

  return <div className="pitch-shell"><div className={`pitch tool-${tool} ${animateTransitions?'is-playing':''} ${playerViewId?'player-view-mode':''}`} aria-busy={animateTransitions} ref={pitchRef} onContextMenu={event=>event.preventDefault()} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => { setDrawing(undefined); objectDrag.current = undefined; playerInteractionStarted.current=false }} onDragOver={event => event.preventDefault()} onDrop={onDrop}>
    <div className="pitch-grain"/>
    <svg className="pitch-markings" viewBox="0 0 68 105" preserveAspectRatio="none" aria-hidden="true">
      <g>
        <rect x=".8" y=".8" width="66.4" height="103.4"/>
        <line x1=".8" y1="52.5" x2="67.2" y2="52.5"/>
        <circle cx="34" cy="52.5" r="9.15"/><circle className="pitch-spot" cx="34" cy="52.5" r=".55"/>
        <rect x="13.85" y=".8" width="40.3" height="16.5"/><rect x="24.84" y=".8" width="18.32" height="5.5"/>
        <circle className="pitch-spot" cx="34" cy="11" r=".55"/><path d="M26.8 17.3 A9.15 9.15 0 0 0 41.2 17.3"/>
        <rect x="13.85" y="87.7" width="40.3" height="16.5"/><rect x="24.84" y="98.7" width="18.32" height="5.5"/>
        <circle className="pitch-spot" cx="34" cy="94" r=".55"/><path d="M26.8 87.7 A9.15 9.15 0 0 1 41.2 87.7"/>
        <path d="M.8 3.2 A2.4 2.4 0 0 0 3.2 .8M64.8 .8A2.4 2.4 0 0 0 67.2 3.2M.8 101.8A2.4 2.4 0 0 1 3.2 104.2M64.8 104.2A2.4 2.4 0 0 1 67.2 101.8"/>
        <rect className="pitch-goal" x="29.34" y=".1" width="9.32" height=".7"/><rect className="pitch-goal" x="29.34" y="104.2" width="9.32" height=".7"/>
      </g>
    </svg>
    <svg className="drawing-layer" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <defs><marker id="arrowhead" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto"><path d="M0,0 L5,2.5 L0,5 z" fill="currentColor"/></marker><marker id="preview-arrowhead" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto"><path d="M0,0 L5,2.5 L0,5 z" fill="currentColor"/></marker></defs>
      {displayFrame.objects.map(object => ['arrow','dashed-arrow','line'].includes(object.type) ? <g key={object.id} data-interactive className={`object-group movable-object ${selectedObjectId===object.id?'selected':''}`} onPointerDown={event=>startObjectDrag(event,object.id)} onContextMenu={event=>{event.preventDefault();event.stopPropagation();removeObject(object.id)}}><line className="object-hit-area" x1={object.x} y1={object.y} x2={object.endX} y2={object.endY}/><line className={`${object.type} object-visible`} x1={object.x} y1={object.y} x2={object.endX} y2={object.endY} markerEnd={object.type !== 'line' ? 'url(#arrowhead)' : undefined}/></g> : object.type === 'zone' ? <g key={object.id} data-interactive className={`object-group movable-object ${selectedObjectId===object.id?'selected':''}`} onPointerDown={event=>startObjectDrag(event,object.id)} onContextMenu={event=>{event.preventDefault();event.stopPropagation();removeObject(object.id)}}><rect className="object-hit-area zone-hit-area" x={object.x-2} y={object.y-2} width={(object.width??0)+4} height={(object.height??0)+4} rx="3"/><rect x={object.x} y={object.y} width={object.width} height={object.height} rx="2" className="zone object-visible"/></g> : null)}
      {previewLine && <line className={`${tool} drawing-preview`} x1={drawing.x} y1={drawing.y} x2={drawing.currentX} y2={drawing.currentY} markerEnd={tool !== 'line' ? 'url(#preview-arrowhead)' : undefined}/>} 
      {previewZone && <rect className="zone drawing-preview" x={Math.min(drawing.x,drawing.currentX)} y={Math.min(drawing.y,drawing.currentY)} width={Math.abs(drawing.currentX-drawing.x)} height={Math.abs(drawing.currentY-drawing.y)} rx="2"/>}
    </svg>
    {drawing && tool === 'ball' && <span className="ball-marker drawing-ghost" style={{left:`${drawing.currentX}%`,top:`${drawing.currentY}%`}}>⚽</span>}
    {drawing && tool === 'text' && <span className="pitch-text drawing-ghost" style={{left:`${drawing.currentX}%`,top:`${drawing.currentY}%`}}>ТАКТИЧЕСКАЯ ЗОНА</span>}
    {displayFrame.objects.filter(object => object.type === 'ball').map(object => <button key={object.id} data-interactive className={`ball-marker movable-object ${selectedObjectId===object.id?'selected':''}`} style={{left:`${object.x}%`,top:`${object.y}%`}} onPointerDown={event => startObjectDrag(event,object.id)} onContextMenu={event=>{event.preventDefault();event.stopPropagation();removeObject(object.id)}} aria-label="Мяч">⚽</button>)}
    {displayFrame.objects.filter(object => object.type === 'text').map(object => <button key={object.id} data-interactive className={`pitch-text movable-object ${selectedObjectId===object.id?'selected':''}`} style={{left:`${object.x}%`,top:`${object.y}%`}} onPointerDown={event => startObjectDrag(event,object.id)} onContextMenu={event=>{event.preventDefault();event.stopPropagation();removeObject(object.id)}}>{object.text}</button>)}
    {displayFrame.placements.map(placement => {
      const player = playerById(placement.playerId)
      if (!player) return null
      const highlighted = playerViewId === player.id
      const muted = Boolean(playerViewId && !highlighted)
      return <button key={player.id} data-interactive className={`player-marker position-${getPositionGroup(player.primaryPosition)} ${selectedPlayerId===player.id?'selected':''} ${highlighted?'player-highlight':''} ${muted?'player-muted':''}`} style={{left:`${placement.x}%`,top:`${placement.y}%`}} title={`${player.nickname||player.name} · ${player.primaryPosition}`} onPointerDown={event => { event.stopPropagation(); event.currentTarget.setPointerCapture(event.pointerId); playerInteractionStarted.current=false; setDraggingPlayer(player.id); onSelectPlayer(player.id); onSelectObject(undefined) }} onPointerMove={event => { if (draggingPlayer===player.id&&pitchRef.current) { if(!playerInteractionStarted.current){onBeginInteraction?.();playerInteractionStarted.current=true} const point=pointFromEvent(event,pitchRef.current); updatePlacement(player.id,point.x,point.y) } }} onPointerUp={() => {setDraggingPlayer(undefined);playerInteractionStarted.current=false}}><span>{player.number}</span><small>{(player.nickname||player.name).slice(0,8)}</small></button>
    })}
  </div></div>
}
